import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  VStack,
  Text,
  Input,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  useToast,
  Card,
  CardBody,

  Spinner,
  Icon,
  HStack,
  Divider
} from '@chakra-ui/react';
import { FaEnvelope, FaLock, FaShieldAlt, FaStar, FaEye } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const PasswordRequest = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const sendPasswordEmail = async (email, password) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/send-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to send email');
      }

      return result;
    } catch (error) {
      console.error('Email sending error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Generate password
      const password = generatePassword();
      
      // Set expiration to 24 hours from now
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 24);

      // Store in database
      const { error: dbError } = await supabase
        .from('password_requests')
        .insert([
          {
            email: email,
            password: password,
            expires_at: expiresAt.toISOString()
          }
        ]);

      if (dbError) {
        throw dbError;
      }

      // Send email
      await sendPasswordEmail(email, password);

      setSuccess(true);
      toast({
        title: "Password sent!",
        description: "Check your email for the access password.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      console.error('Error:', error);
      setError(error.message || 'Failed to send password. Please try again.');
      toast({
        title: "Error",
        description: error.message || 'Failed to send password. Please try again.',
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const accentColor = '#0066cc';

  if (success) {
    return (
      <Box 
        bg="linear-gradient(135deg, #1e40af 0%, #0066cc 100%)" 
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Container maxW="md">
          <Card bg="white" shadow="2xl" borderRadius="2xl">
            <CardBody p={8} textAlign="center">
              <VStack spacing={6}>
                <Box 
                  bg="green.100" 
                  p={4} 
                  borderRadius="full"
                  mb={2}
                >
                  <Icon as={FaShieldAlt} w={8} h={8} color="green.600" />
                </Box>
                
                <Heading size="lg" color="gray.800">
                  Password Sent Successfully!
                </Heading>
                
                <Text color="gray.600" fontSize="lg" lineHeight="1.6">
                  We've sent your access password to <Text as="span" fontWeight="bold" color={accentColor}>{email}</Text>. 
                  Please check your email and use the password to access the presentation.
                </Text>

                <Button
                  as={RouterLink} 
                  to="/pitch-deck"
                  bg={accentColor}
                  color="white"
                  size="lg"
                  w="100%"
                  h="54px"
                  leftIcon={<FaLock />}
                  _hover={{ 
                    transform: "translateY(-2px)",
                    shadow: "lg"
                  }}
                  borderRadius="xl"
                  fontWeight="600"
                  px={8}
                >
                  Access Presentation →
                </Button>

                <Divider />

                <Text fontSize="sm" color="gray.600" textAlign="center" lineHeight="1.6">
                  🔐 This exclusive presentation contains confidential business information.<br/>
                  Please keep your access credentials secure and do not share them.
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box 
      bg="linear-gradient(135deg, #1e40af 0%, #0066cc 100%)" 
      minH="100vh"
    >
      <Container maxW="7xl" h="100vh">
        {/* Desktop Two-Panel Layout */}
        <Box 
          display={{ base: "none", lg: "flex" }} 
          h="100vh" 
          alignItems="center"
        >
          {/* Left Panel - Hero Content */}
          <VStack 
            flex="1"
            spacing={8}
            pr={12}
            textAlign="left"
            alignItems="flex-start"
            justifyContent="center"
            h="100%"
          >
            {/* Logo */}
            <Box>
              <Box 
                bg="white"
                px={8}
                py={4}
                borderRadius="xl"
                shadow="xl"
                display="inline-block"
                border="3px solid"
                borderColor="blue.100"
              >
                <Text 
                  fontSize="2xl" 
                  fontWeight="800" 
                  color="#0066cc"
                  letterSpacing="tight"
                >
                  AtHomeBloodDraw.com
                </Text>
              </Box>
            </Box>

            {/* Exclusive Badge */}
            <Box 
              bg="linear-gradient(135deg, #e6f3ff 0%, #cce7ff 100%)"
              px={6}
              py={3}
              borderRadius="full"
              border="2px solid"
              borderColor="blue.200"
            >
              <HStack spacing={2}>
                <Icon as={FaStar} color="blue.600" boxSize={4} />
                <Text fontSize="sm" fontWeight="bold" color="blue.800" letterSpacing="wide">
                  EXCLUSIVE INVITATION
                </Text>
                <Icon as={FaStar} color="blue.600" boxSize={4} />
              </HStack>
            </Box>

            {/* Main Heading */}
            <VStack spacing={6} alignItems="flex-start">
              <Heading 
                size="3xl" 
                color="white"
                fontWeight="800"
                lineHeight="1.2"
              >
                Pitch Deck
                <br />
                <Text as="span" color="cyan.300">Presentation</Text>
              </Heading>
              
              <Text color="blue.100" fontSize="xl" lineHeight="1.6" maxW="lg">
                You have been <Text as="span" fontWeight="bold" color="cyan.300">exclusively invited</Text> to view our 
                comprehensive pitch deck presentation. This exclusive presentation showcases our innovative 
                platform and market opportunity.
              </Text>
            </VStack>

            {/* Features */}
            <VStack spacing={4} alignItems="flex-start">
              <HStack spacing={4}>
                <Box 
                  bg="rgba(255, 255, 255, 0.2)" 
                  p={3} 
                  borderRadius="full"
                  backdropFilter="blur(10px)"
                >
                  <Icon as={FaEye} color="white" boxSize={5} />
                </Box>
                <VStack alignItems="flex-start" spacing={0}>
                  <Text color="white" fontWeight="bold" fontSize="lg">Live Interactive Presentation</Text>
                  <Text color="blue.200" fontSize="sm">Real-time data and dynamic content</Text>
                </VStack>
              </HStack>
              
              <HStack spacing={4}>
                <Box 
                  bg="rgba(255, 255, 255, 0.2)" 
                  p={3} 
                  borderRadius="full"
                  backdropFilter="blur(10px)"
                >
                  <Icon as={FaLock} color="white" boxSize={5} />
                </Box>
                <VStack alignItems="flex-start" spacing={0}>
                  <Text color="white" fontWeight="bold" fontSize="lg">24-Hour Secure Access</Text>
                  <Text color="blue.200" fontSize="sm">Your password provides exclusive access for 24 hours only</Text>
                </VStack>
              </HStack>
            </VStack>
          </VStack>

          {/* Right Panel - Form */}
          <Box flex="1" display="flex" alignItems="center" justifyContent="center">
            <Box w="100%" maxW="md">
              <Card 
                bg="white" 
                shadow="2xl" 
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.100"
                overflow="hidden"
              >
                <Box 
                  bg="linear-gradient(135deg, #1e40af 0%, #0066cc 100%)" 
                  p={6} 
                  textAlign="center"
                >
                  <VStack spacing={3}>
                    <Box 
                      bg="white" 
                      p={3} 
                      borderRadius="full"
                      shadow="lg"
                    >
                      <Icon as={FaShieldAlt} w={8} h={8} color="blue.600" />
                    </Box>
                    <Heading size="lg" color="white" fontWeight="700">
                      Get Your Access Password
                    </Heading>
                    <Text color="blue.100" fontSize="sm" maxW="sm">
                      Enter your email to receive instant access to our exclusive presentation. 
                      Your password will be valid for 24 hours only.
                    </Text>
                  </VStack>
                </Box>
                
                <CardBody p={6}>
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={6}>
                      {error && (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon />
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}

                      <VStack spacing={4} w="100%">
                        <Box w="100%">
                          <Text mb={3} fontWeight="600" color="gray.700" fontSize="md">
                            Professional Email Address
                          </Text>
                          <Input
                            type="email"
                            placeholder="Enter your professional email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            size="lg"
                            disabled={loading}
                            required
                            bg="gray.50"
                            border="2px solid"
                            borderColor="gray.200"
                            borderRadius="xl"
                            _hover={{ borderColor: "blue.300", bg: "white" }}
                            _focus={{ 
                              borderColor: "blue.500", 
                              boxShadow: "0 0 0 1px #0066cc",
                              bg: "white"
                            }}
                            _placeholder={{ color: "gray.400" }}
                            fontSize="md"
                            h="54px"
                          />
                        </Box>

                        <Button
                          type="submit"
                          bg="linear-gradient(135deg, #1e40af 0%, #0066cc 100%)"
                          color="white"
                          size="lg"
                          w="100%"
                          h="54px"
                          leftIcon={loading ? <Spinner size="sm" /> : <FaEnvelope />}
                          isLoading={loading}
                          loadingText="Sending Password..."
                          _hover={{ 
                            transform: "translateY(-2px)",
                            shadow: "xl"
                          }}
                          _active={{ transform: "translateY(0)" }}
                          borderRadius="xl"
                          fontWeight="600"
                          fontSize="md"
                          transition="all 0.2s"
                        >
                          Send Access Password
                        </Button>
                      </VStack>

                      <Box 
                        pt={4} 
                        borderTop="1px solid" 
                        borderColor="gray.200"
                        textAlign="center"
                      >
                        <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                          🔒 Your password will be sent securely to your email<br/>
                          ⏰ Access expires in 24 hours for security
                        </Text>
                      </Box>
                    </VStack>
                  </form>
                </CardBody>
              </Card>
            </Box>
          </Box>
        </Box>

        {/* Mobile Layout */}
        <Box 
          display={{ base: "block", lg: "none" }} 
          py={8}
        >
          <VStack spacing={8}>
            {/* Mobile Logo */}
            <VStack spacing={4} textAlign="center">
              <Box 
                bg="white" 
                p={4} 
                borderRadius="xl" 
                shadow="lg"
                border="2px solid"
                borderColor="blue.200"
              >
                <Text 
                  fontSize="xl" 
                  fontWeight="800" 
                  color="#0066cc"
                  letterSpacing="tight"
                >
                  AtHomeBloodDraw.com
                </Text>
              </Box>
              
              <Box 
                bg="linear-gradient(135deg, #e6f3ff 0%, #cce7ff 100%)"
                px={4}
                py={2}
                borderRadius="full"
                border="2px solid"
                borderColor="blue.200"
              >
                <HStack spacing={2}>
                  <Icon as={FaStar} color="blue.600" boxSize={3} />
                  <Text fontSize="xs" fontWeight="bold" color="blue.800" letterSpacing="wide">
                    EXCLUSIVE INVITATION
                  </Text>
                  <Icon as={FaStar} color="blue.600" boxSize={3} />
                </HStack>
              </Box>
              
              <Heading 
                size="xl" 
                color="white"
                fontWeight="800"
                textAlign="center"
              >
                Pitch Deck
                <br />
                <Text as="span" color="cyan.300">Presentation</Text>
              </Heading>
              
              <Text color="blue.100" fontSize="md" lineHeight="1.6" textAlign="center" px={4}>
                You have been <Text as="span" fontWeight="bold" color="cyan.300">exclusively invited</Text> to view our 
                comprehensive pitch deck presentation.
              </Text>
            </VStack>

            {/* Mobile Form */}
            <Box px={4}>
              <Card 
                bg="white" 
                w="100%" 
                maxW="md" 
                mx="auto"
                shadow="2xl" 
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.100"
                overflow="hidden"
              >
                <Box 
                  bg="linear-gradient(135deg, #1e40af 0%, #0066cc 100%)" 
                  p={6} 
                  textAlign="center"
                >
                  <VStack spacing={3}>
                    <Box 
                      bg="white" 
                      p={3} 
                      borderRadius="full"
                      shadow="lg"
                    >
                      <Icon as={FaShieldAlt} w={8} h={8} color="blue.600" />
                    </Box>
                    <Heading size="lg" color="white" fontWeight="700">
                      Get Your Access Password
                    </Heading>
                    <Text color="blue.100" fontSize="sm" maxW="sm">
                      Enter your email to receive instant access to our exclusive presentation. 
                      Your password will be valid for 24 hours.
                    </Text>
                  </VStack>
                </Box>
                
                <CardBody p={6}>
                  <form onSubmit={handleSubmit}>
                    <VStack spacing={6}>
                      {error && (
                        <Alert status="error" borderRadius="md">
                          <AlertIcon />
                          <AlertDescription>{error}</AlertDescription>
                        </Alert>
                      )}

                      <VStack spacing={4} w="100%">
                        <Box w="100%">
                          <Text mb={3} fontWeight="600" color="gray.700" fontSize="md">
                            Professional Email Address
                          </Text>
                          <Input
                            type="email"
                            placeholder="Enter your professional email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            size="lg"
                            disabled={loading}
                            required
                            bg="gray.50"
                            border="2px solid"
                            borderColor="gray.200"
                            borderRadius="xl"
                            _hover={{ borderColor: "blue.300", bg: "white" }}
                            _focus={{ 
                              borderColor: "blue.500", 
                              boxShadow: "0 0 0 1px #0066cc",
                              bg: "white"
                            }}
                            _placeholder={{ color: "gray.400" }}
                            fontSize="md"
                            h="54px"
                          />
                        </Box>

                        <Button
                          type="submit"
                          bg="linear-gradient(135deg, #1e40af 0%, #0066cc 100%)"
                          color="white"
                          size="lg"
                          w="100%"
                          h="54px"
                          leftIcon={loading ? <Spinner size="sm" /> : <FaEnvelope />}
                          isLoading={loading}
                          loadingText="Sending Password..."
                          _hover={{ 
                            transform: "translateY(-2px)",
                            shadow: "xl"
                          }}
                          _active={{ transform: "translateY(0)" }}
                          borderRadius="xl"
                          fontWeight="600"
                          fontSize="md"
                          transition="all 0.2s"
                        >
                          Send Access Password
                        </Button>
                      </VStack>

                      <Box 
                        pt={4} 
                        borderTop="1px solid" 
                        borderColor="gray.200"
                        textAlign="center"
                      >
                        <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                          🔒 Your password will be sent securely to your email<br/>
                          ⏰ Access expires in 24 hours for security
                        </Text>
                      </Box>
                    </VStack>
                  </form>
                </CardBody>
              </Card>
            </Box>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
};

export default PasswordRequest; 